import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import VentasCatalogSelector from './VentasCatalogSelector';
import ServiceSelector from '../ServiceOrders/ServiceSelector';
import SupplierSelector from '../PurchaseOrders/SupplierSelector';
import CreateSupplierModal from '../PurchaseOrders/CreateSupplierModal';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import PriceIgvHint from '../Common/PriceIgvHint';
import SubtotalQuantityModeToggle from '../Common/SubtotalQuantityModeToggle';
import LineItemField from '../Common/LineItemField';
import UnitPriceTextInput from '../Common/UnitPriceTextInput';
import QuantityTextInput from '../Common/QuantityTextInput';
import QuickProductLineBar from '../Common/QuickProductLineBar';
import ProductLineDefaultsBar from '../Common/ProductLineDefaultsBar';
import ComprasItemsImportPreviewModal from '../Common/ComprasItemsImportPreviewModal';
import FormLoadingSpinner from '../Common/FormLoadingSpinner';
import Toast from '../Common/Toast';
import { useSalesPointOfSale } from '../../contexts/SalesPointOfSaleContext';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { salesQuoteService } from '../../services/salesQuoteService';
import { salesOrderService } from '../../services/salesOrderService';
import salesServiceOrderService from '../../services/salesServiceOrderService';
import { getSalePriceTypes } from '../../services/salesPriceTypeService';
import { getSalesPriceList } from '../../services/salesService';
import salesContinuousServiceService from '../../services/salesContinuousServiceService';
import entryGuideService from '../../services/entryGuideService';
import { supplierService } from '../../services/supplierService';
import { useVentasProductCatalog } from '../../hooks/useVentasProductCatalog';
import { adjustNetPriceOnHasIgvChange, grossToNet, isItemTaxable, netToGross, roundMoney, roundPrice } from '../../utils/priceIgvUtils';
import { handlePreventEnterFormSubmit } from '../../utils/preventEnterFormSubmit';
import {
  applyDiscountOfferFields,
  buildOfferCatalogEntry,
  calcVentasHeader,
  calcVentasLine,
  clearDiscountOfferFields,
  copyOfferLineMetadata,
  formatIgvRateLabel,
  getItemCatalogSelectionValue,
  getProductPosGrossPrice,
  isMultiProductOfferLine,
  materializeLineFromOffer,
  parseIgvRate,
  repriceItemFromOfferMetadata,
  resolvePosListGrossPrice,
  resolveServicePosListPrice,
  applyServiceListPriceToLine,
  ventasPosPriceToUnitNet,
} from '../../utils/ventasIgvUtils';
import { getItemLineSubtotal, itemHasCustomConversion } from '../../utils/unitSelectUtils';
import {
  downloadPurchaseOrderItemsTemplate,
  parsePurchaseOrderItemsExcel,
} from '../../utils/comprasItemsExcelImport';
import { emptyPurchaseOrderStagingItem, emptyVentasServiceItem } from '../../utils/quickProductLineFactories';
import {
  applyProductLineDefaults,
  buildProductLineSettings,
  emptyStagingWithDefaults,
  mergeProductLineDefaults,
} from '../../utils/productLineDefaults';
import { PO_ITEM_ROW, POIC } from '../../utils/purchaseOrderItemRowLayout';
import PaymentConditionSection from '../Common/PaymentConditionSection';
import DualCurrencyHint from '../Common/DualCurrencyHint';
import { useExchangeRateForDate } from '../../hooks/useExchangeRateForDate';
import IgvRateSelect from './IgvRateSelect';
import VentasOrderReferenceSelectorModal from './VentasOrderReferenceSelectorModal';
import { VENTAS_DOCUMENT_TYPES, ventasPaymentMethodNeedsOperation } from '../../utils/ventasSalesConstants';

const stripImportMeta = ({ _label, _unitFormula, _unitIsCustom, ...item }) => item;

/** En cotizaciones y ventas los precios de línea siempre se ingresan sin IGV. */
const VENTAS_PRICES_INCLUDE_IGV = false;

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

const VentasCommercialForm = ({
  mode = 'quote',
  entity = null,
  onSuccess,
  onClose,
  readOnly = false,
  isPageMode = true,
  initialIssueDate = null,
}) => {
  const isQuote = mode === 'quote';
  const isServiceOrder = mode === 'service_order';
  const isQuoteLike = isQuote || isServiceOrder;
  const service = isQuote
    ? salesQuoteService
    : isServiceOrder
      ? salesServiceOrderService
      : salesOrderService;
  const { selectedPointOfSale } = useSalesPointOfSale();
  const { productConversionsEnabled, quickProductLineEnabled, productsIgvEnabled } =
    useModuleSettings();

  const [defaultLineProjectId, setDefaultLineProjectId] = useState('');
  const [posDefaultProjectId, setPosDefaultProjectId] = useState('');
  const [posSeriesByType, setPosSeriesByType] = useState({});
  const [peekedSeriesNumber, setPeekedSeriesNumber] = useState(null);
  const [closedShiftDates, setClosedShiftDates] = useState([]);
  const [defaultLineHasIgv, setDefaultLineHasIgv] = useState(true);
  const [salePriceTypes, setSalePriceTypes] = useState([]);
  const [defaultLineSaleTypeId, setDefaultLineSaleTypeId] = useState('');
  const productLineSettings = useMemo(
    () =>
      buildProductLineSettings({
        productsProjectEnabled: true,
        productsIgvEnabled,
        defaultProjectId: defaultLineProjectId,
        defaultHasIgv: defaultLineHasIgv,
      }),
    [productsIgvEnabled, defaultLineProjectId, defaultLineHasIgv],
  );
  const showProjectSelector = true;
  const productFieldClass = showProjectSelector ? POIC.product : POIC.productWide;
  const showProductLineDefaults = showProjectSelector || productsIgvEnabled || salePriceTypes.length > 0;
  const hasSaleTypes = salePriceTypes.length > 0;

  const [formData, setFormData] = useState({
    client_id: '',
    issue_date: initialIssueDate || new Date().toISOString().split('T')[0],
    currency: 'PEN',
    valid_until: '',
    igv_rate: '18',
    notes: '',
    delivery_date: '',
    service_start_date: '',
    service_end_date: '',
    payment_condition_type: 'cash',
    payment_condition_days: 30,
    payment_condition_date: '',
    payment_method: '',
    operation_number: '',
    document_type: 'factura',
    numbering_mode: 'auto',
    series: '',
    number: '',
    items: [],
  });
  const [linkedQuotes, setLinkedQuotes] = useState([]);
  const [linkedServiceOrders, setLinkedServiceOrders] = useState([]);
  const [linkedContinuousServices, setLinkedContinuousServices] = useState([]);
  const [linkedExitGuides, setLinkedExitGuides] = useState([]);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const [referencesLoading, setReferencesLoading] = useState(false);
  const [pendingReferenceQuotes, setPendingReferenceQuotes] = useState([]);
  const [pendingReferenceServiceOrders, setPendingReferenceServiceOrders] = useState([]);
  const [pendingReferenceContinuousServices, setPendingReferenceContinuousServices] = useState([]);
  const [pendingReferenceExitGuides, setPendingReferenceExitGuides] = useState([]);
  const [exitGuidesSnapshotAtOpen, setExitGuidesSnapshotAtOpen] = useState([]);
  const [quotesSnapshotAtOpen, setQuotesSnapshotAtOpen] = useState([]);
  const [serviceOrdersSnapshotAtOpen, setServiceOrdersSnapshotAtOpen] = useState([]);
  const [exitGuideItemsWarning, setExitGuideItemsWarning] = useState('');
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formDataLoading, setFormDataLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [useConvertedQuantityForSubtotal, setUseConvertedQuantityForSubtotal] = useState(true);
  const [isEditingAmounts, setIsEditingAmounts] = useState(false);
  const [amountOverride, setAmountOverride] = useState(null);
  const [amountForm, setAmountForm] = useState({ igv_amount: '' });
  const [importingItems, setImportingItems] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [stagingItem, setStagingItem] = useState(() => emptyPurchaseOrderStagingItem());
  const [stagingSelectionId, setStagingSelectionId] = useState('');
  const [stagingError, setStagingError] = useState('');
  const hasLoadedFormData = useRef(false);
  const hasLoadedEntity = useRef(false);
  const syncedStagingDefaultsRef = useRef({
    hasIgv: null,
    projectId: null,
    saleTypeId: null,
  });
  const itemsImportFileRef = useRef(null);
  const stagingProductSelectorRef = useRef(null);

  const {
    products,
    catalogEntries,
    getProduct: getCatalogProduct,
    getCatalogEntry,
    mergeProducts,
    mergeOffers,
    ensureByIds,
    searchProducts,
  } = useVentasProductCatalog(selectedPointOfSale?.id);

  const findProductById = useCallback(
    (id, fallback = null) => getCatalogProduct(id, fallback),
    [getCatalogProduct],
  );

  const findServiceById = (id) => services.find((s) => s.id === parseInt(id, 10)) || null;

  const isOfferCatalogValue = useCallback(
    (value) => {
      const key = String(value ?? '');
      if (!key) return false;
      if (key.startsWith('offer-')) return true;
      return getCatalogEntry(value)?.catalog_type === 'offer';
    },
    [getCatalogEntry],
  );

  const getStagingLineDefaults = useCallback(
    (prev) => {
      const defaults = applyProductLineDefaults(productLineSettings);
      return {
        project_id: defaults.project_id !== undefined ? defaults.project_id : prev.project_id,
        has_igv: defaults.has_igv !== undefined ? defaults.has_igv : prev.has_igv,
        sales_price_type_id:
          defaultLineSaleTypeId || prev.sales_price_type_id || '',
      };
    },
    [productLineSettings, defaultLineSaleTypeId],
  );

  const materializeStagingFromOffer = useCallback(
    (offer, prev) =>
      materializeLineFromOffer(offer, prev, {
        igvRate: parseIgvRate(formData.igv_rate),
        lineDefaults: getStagingLineDefaults(prev),
      }),
    [formData.igv_rate, getStagingLineDefaults],
  );

  const resolveStagingLineForAdd = useCallback(() => {
    if (stagingItem.sales_offer_id) return stagingItem;
    const selectionKey = stagingSelectionId || getItemCatalogSelectionValue(stagingItem);
    if (!isOfferCatalogValue(selectionKey)) return stagingItem;
    const offer = getCatalogEntry(selectionKey);
    if (offer?.catalog_type !== 'offer') return stagingItem;
    return materializeStagingFromOffer(offer, stagingItem);
  }, [
    stagingItem,
    stagingSelectionId,
    isOfferCatalogValue,
    getCatalogEntry,
    materializeStagingFromOffer,
  ]);

  const clearStagingProductSelection = useCallback(() => {
    setStagingSelectionId('');
    setStagingItem((prev) =>
      clearDiscountOfferFields({
        ...prev,
        product_id: '',
        unit_price: '',
        unit_of_measure: '',
      }),
    );
  }, []);

  const applyProductListPrice = (item, product, igvRateValue) => {
    const repricedFromOffer = repriceItemFromOfferMetadata(item, parseIgvRate(igvRateValue));
    if (repricedFromOffer) return repricedFromOffer;

    const listGross = resolvePosListGrossPrice(product, item.sales_price_type_id);
    const offerGross =
      product?.effective_price != null && product.effective_price !== ''
        ? parseFloat(product.effective_price)
        : listGross;

    if (listGross === null || !Number.isFinite(listGross)) return item;

    const taxable = isItemTaxable(item.has_igv ?? true);
    const grossSource =
      taxable && offerGross != null && Number.isFinite(offerGross) ? offerGross : listGross;

    const net = ventasPosPriceToUnitNet(grossSource, {
      hasIgv: item.has_igv ?? true,
      igvRate: igvRateValue,
    });
    if (net === '') return item;

    let updated = { ...item, unit_price: net };
    if (
      offerGross != null
      && Number.isFinite(offerGross)
      && Number.isFinite(listGross)
      && offerGross < listGross
      && (product?.offer_kind == null || product?.offer_kind === 'discount')
    ) {
      updated = applyDiscountOfferFields(updated, {
        baseGross: listGross,
        offerGross,
        offerId: product?.offer_id ?? null,
        offerName: product?.offer_name ?? null,
        offerCode: product?.offer_code ?? null,
        catalogSelectionId: product?.offer_id ? `offer-${product.offer_id}` : null,
        discountPercent: product?.discount_percent ?? null,
      });
    } else if (!item?.sales_offer_id) {
      updated = clearDiscountOfferFields(updated);
    } else {
      updated = copyOfferLineMetadata(item, updated);
    }
    return updated;
  };

  const applyServiceListPrice = (item, service, igvRateValue) =>
    applyServiceListPriceToLine(item, service, selectedPointOfSale?.id, igvRateValue);

  const repriceLineOnIgvToggle = (item, oldHasIgv, newHasIgv, igvRateValue) => {
    if (oldHasIgv === newHasIgv) return item;
    const currentNet = parseFloat(item.unit_price);
    if (!Number.isFinite(currentNet) || currentNet <= 0) return item;
    if (oldHasIgv && !newHasIgv) {
      return { ...item, unit_price: roundPrice(netToGross(currentNet, igvRateValue)) };
    }
    if (!oldHasIgv && newHasIgv) {
      return { ...item, unit_price: grossToNet(currentNet, igvRateValue) };
    }
    return item;
  };

  const applyOfferToStaging = (offer) => {
    if (!offer || offer.catalog_type !== 'offer') return;
    setStagingError('');
    mergeOffers([offer]);
    setStagingSelectionId(String(offer.id));
    setStagingItem((prev) => materializeStagingFromOffer(offer, prev));
    if (offer.offer_kind === 'multi_product') {
      const firstItem = offer.items?.[0];
      if (firstItem) {
        mergeProducts([
          {
            id: firstItem.product_id,
            sku: firstItem.sku,
            name: firstItem.name,
            unit_of_measure: firstItem.unit_of_measure,
          },
        ]);
      }
    } else if (offer.offer_kind === 'discount' && offer.product_id) {
      const offerItem = offer.items?.[0];
      mergeProducts([
        {
          id: offer.product_id,
          sku: offerItem?.sku,
          name: offerItem?.name,
          unit_of_measure: offer.unit_of_measure || offerItem?.unit_of_measure,
          list_price: offerItem?.base_price,
          effective_price: offerItem?.offer_price ?? offer.effective_price,
          offer_id: offer.offer_id,
          offer_name: offer.name,
          offer_code: offer.sku,
        },
      ]);
    }
  };

  const applyOfferToItemRow = (index, offer) => {
    if (!offer || offer.catalog_type !== 'offer') return;
    mergeOffers([offer]);

    if (offer.offer_kind === 'discount' && offer.product_id) {
      const offerItem = offer.items?.[0];
      mergeProducts([
        {
          id: offer.product_id,
          sku: offerItem?.sku,
          name: offerItem?.name,
          unit_of_measure: offer.unit_of_measure || offerItem?.unit_of_measure,
          list_price: offerItem?.base_price,
          effective_price: offerItem?.offer_price ?? offer.effective_price,
          offer_id: offer.offer_id,
          offer_name: offer.name,
          offer_code: offer.sku,
        },
      ]);
    } else if (offer.offer_kind === 'multi_product') {
      const firstItem = offer.items?.[0];
      if (firstItem) {
        mergeProducts([
          {
            id: firstItem.product_id,
            sku: firstItem.sku,
            name: firstItem.name,
            unit_of_measure: firstItem.unit_of_measure,
          },
        ]);
      }
    }

    setAmountOverride(null);
    setFormData((prev) => {
      const items = [...prev.items];
      const updatedItem = materializeLineFromOffer(offer, items[index], {
        igvRate: parseIgvRate(prev.igv_rate),
        lineDefaults: getStagingLineDefaults(items[index]),
      });
      items[index] = updatedItem;
      return { ...prev, items };
    });
  };

  const productSelectorAsyncProps = {
    asyncSearch: true,
    knownProducts: catalogEntries,
    onSearchProducts: searchProducts,
    onEnsureProduct: (id) => ensureByIds([id]),
  };

  const stagingCatalogValue =
    stagingSelectionId || getItemCatalogSelectionValue(stagingItem) || '';

  const lineOptions = useMemo(
    () => ({
      productConversionsEnabled,
      useConvertedQuantityForSubtotal,
      getProduct: findProductById,
    }),
    [productConversionsEnabled, useConvertedQuantityForSubtotal, getCatalogProduct],
  );

  const igvRate = parseIgvRate(formData.igv_rate);
  const { saleRate } = useExchangeRateForDate(formData.issue_date);

  const calculatedTotals = useMemo(() => {
    const header = calcVentasHeader(formData.items, igvRate, lineOptions);
    return {
      baseImponible: header.base_imponible,
      baseImponibleNeta: header.base_imponible_neta,
      descuento: header.descuento,
      descuentoGravado: header.descuento_gravado,
      descuentoNoGravado: header.descuento_no_gravado,
      igv: header.igv_amount,
      noGravados: header.no_gravados,
      noGravadosBruto: header.no_gravados_bruto,
      total: header.total_amount,
    };
  }, [formData.items, igvRate, lineOptions]);

  const displayTotals = amountOverride || calculatedTotals;

  const applySalePriceTypes = useCallback((types = []) => {
    const list = Array.isArray(types) ? types : [];
    setSalePriceTypes(list);
    const def = list.find((t) => t.is_default) || list[0];
    setDefaultLineSaleTypeId(def ? String(def.id) : '');
  }, []);

  const loadSalePriceTypes = useCallback(
    async (posId) => {
      if (!posId) {
        applySalePriceTypes([]);
        return;
      }
      try {
        const res = await getSalePriceTypes(Number(posId), true);
        let types = res?.success ? res.data || [] : [];
        if (!types.length) {
          const listRes = await getSalesPriceList(Number(posId), '', 1, 1, 'prices');
          types = listRes?.settings?.sale_price_types || [];
        }
        applySalePriceTypes(types);
      } catch {
        try {
          const listRes = await getSalesPriceList(Number(posId), '', 1, 1, 'prices');
          applySalePriceTypes(listRes?.settings?.sale_price_types || []);
        } catch {
          applySalePriceTypes([]);
        }
      }
    },
    [applySalePriceTypes],
  );

  const hasProductItems = useMemo(
    () => formData.items.some((item) => item.item_type !== 'service'),
    [formData.items],
  );
  const hasServiceItems = useMemo(
    () => formData.items.some((item) => item.item_type === 'service'),
    [formData.items],
  );

  useEffect(() => {
    const hasDelivery = formData.delivery_date?.trim();
    const hasServiceDates =
      formData.service_start_date?.trim() && formData.service_end_date?.trim();
    if (!hasDelivery && !hasServiceDates) return;
    setErrors((prev) => {
      if (!prev.delivery_date && !prev.service_start_date) return prev;
      const next = { ...prev };
      delete next.delivery_date;
      delete next.service_start_date;
      return next;
    });
  }, [formData.delivery_date, formData.service_start_date, formData.service_end_date]);

  useEffect(() => {
    if (!showProductLineDefaults) return;

    setStagingItem((prev) => {
      const synced = syncedStagingDefaultsRef.current;
      let merged = { ...prev };
      let oldHasIgv = prev.has_igv ?? true;
      let shouldRepriceIgv = false;
      let changed = false;

      if (showProjectSelector && defaultLineProjectId !== synced.projectId) {
        if (merged.project_id !== defaultLineProjectId) {
          merged.project_id = defaultLineProjectId;
          changed = true;
        }
      } else if (!showProjectSelector && posDefaultProjectId) {
        if (merged.project_id !== posDefaultProjectId) {
          merged.project_id = posDefaultProjectId;
          changed = true;
        }
      }

      if (defaultLineSaleTypeId && defaultLineSaleTypeId !== synced.saleTypeId) {
        if (merged.sales_price_type_id !== defaultLineSaleTypeId) {
          merged.sales_price_type_id = defaultLineSaleTypeId;
          changed = true;
        }
      }

      if (productsIgvEnabled && defaultLineHasIgv !== synced.hasIgv) {
        if (merged.has_igv !== defaultLineHasIgv) {
          merged.has_igv = defaultLineHasIgv;
          changed = true;
        }
        shouldRepriceIgv = true;
      }

      syncedStagingDefaultsRef.current = {
        hasIgv: productsIgvEnabled ? defaultLineHasIgv : synced.hasIgv,
        projectId: showProjectSelector ? defaultLineProjectId : posDefaultProjectId || synced.projectId,
        saleTypeId: defaultLineSaleTypeId || synced.saleTypeId,
      };

      if (shouldRepriceIgv && prev.product_id) {
        const newHasIgv = merged.has_igv ?? true;
        const product = findProductById(prev.product_id);
        const listGross = resolvePosListGrossPrice(product, merged.sales_price_type_id);
        if (listGross != null && Number.isFinite(listGross)) {
          merged = applyProductListPrice(merged, product, formData.igv_rate);
        } else {
          merged = repriceLineOnIgvToggle(merged, oldHasIgv, newHasIgv, formData.igv_rate);
        }
        changed = true;
      }

      return changed ? merged : prev;
    });
  }, [
    showProductLineDefaults,
    showProjectSelector,
    productsIgvEnabled,
    defaultLineProjectId,
    posDefaultProjectId,
    defaultLineHasIgv,
    defaultLineSaleTypeId,
    formData.igv_rate,
    findProductById,
  ]);

  const applyPosDefaultProject = useCallback((defaultProjectId) => {
    if (defaultProjectId) {
      const id = String(defaultProjectId);
      setPosDefaultProjectId(id);
      setDefaultLineProjectId(id);
      return;
    }
    setPosDefaultProjectId('');
  }, []);

  const applyPosFormDefaults = useCallback((data) => {
    applyPosDefaultProject(data?.default_project_id);
    setPosSeriesByType(data?.series_by_type || {});
    setClosedShiftDates(data?.closed_shift_dates || []);
  }, [applyPosDefaultProject]);

  useEffect(() => {
    loadSalePriceTypes(selectedPointOfSale?.id);
  }, [selectedPointOfSale?.id, loadSalePriceTypes]);

  useEffect(() => {
    if (isQuoteLike || entity?.id || readOnly) return;
    if (formData.numbering_mode !== 'auto') {
      setPeekedSeriesNumber(null);
      return;
    }
    if (!selectedPointOfSale?.id || !formData.document_type) {
      setPeekedSeriesNumber(null);
      return;
    }

    salesOrderService
      .peekSeries(selectedPointOfSale.id, formData.document_type)
      .then((res) => {
        if (res.success && res.data) {
          setPeekedSeriesNumber(res.data);
        } else {
          setPeekedSeriesNumber(null);
        }
      })
      .catch(() => setPeekedSeriesNumber(null));
  }, [
    isQuoteLike,
    entity?.id,
    readOnly,
    selectedPointOfSale?.id,
    formData.document_type,
    formData.numbering_mode,
  ]);

  useEffect(() => {
    if (!selectedPointOfSale?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await service.getFormData(selectedPointOfSale.id);
        if (!cancelled && res.success) {
          applyPosFormDefaults(res.data);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPointOfSale?.id, service, applyPosFormDefaults]);

  useEffect(() => {
    if (isPageMode && !hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, [isPageMode]);

  useEffect(() => {
    const ids = (formData.items || []).map((item) => item.product_id).filter(Boolean);
    if (ids.length > 0) ensureByIds(ids).catch(() => {});
  }, [formData.items, ensureByIds]);

  useEffect(() => {
    const entries = [];
    const pushOfferEntry = (lineItem) => {
      if (!lineItem?.sales_offer_id) return;
      const catalogKey = lineItem.catalog_selection_id || `offer-${lineItem.sales_offer_id}`;
      const catalogOffer = getCatalogEntry(catalogKey);
      if (catalogOffer?.catalog_type === 'offer') {
        entries.push(catalogOffer);
        return;
      }
      const built = buildOfferCatalogEntry(lineItem, findProductById(lineItem.product_id));
      if (built) entries.push(built);
    };
    pushOfferEntry(stagingItem);
    (formData.items || []).forEach(pushOfferEntry);
    if (entries.length > 0) mergeOffers(entries);
  }, [formData.items, stagingItem, mergeOffers, getCatalogEntry, getCatalogProduct]);

  useEffect(() => {
    if (entity?.id && !hasLoadedEntity.current) {
      hasLoadedEntity.current = true;
      setFormData({
        client_id: entity.client_id?.toString() || '',
        issue_date: formatDateForInput(entity.issue_date) || new Date().toISOString().split('T')[0],
        currency: entity.currency || 'PEN',
        valid_until: formatDateForInput(entity.valid_until) || '',
        igv_rate: String(entity.items?.[0]?.igv_rate ?? '18'),
        notes: entity.notes || '',
        delivery_date: formatDateForInput(entity.delivery_date) || '',
        service_start_date: formatDateForInput(entity.service_start_date) || '',
        service_end_date: formatDateForInput(entity.service_end_date) || '',
        payment_condition_type: entity.payment_condition_type || 'cash',
        payment_condition_days: entity.payment_condition_days ?? 30,
        payment_condition_date: formatDateForInput(entity.payment_condition_date) || '',
        payment_method: entity.payment_method || '',
        operation_number: entity.operation_number || '',
        document_type: entity.document_type || 'factura',
        items: (entity.items || []).map((item) => ({
          item_type: item.item_type || (item.service_id ? 'service' : 'product'),
          product_id: item.product_id?.toString() || '',
          service_id: item.service_id?.toString() || '',
          description: item.notes || item.description || '',
          unit_of_measure: item.unit_of_measure || item.product?.unit_of_measure || item.service?.unit_of_measure || '',
          sales_price_type_id: item.sales_price_type_id?.toString() || '',
          project_id: item.project_id?.toString() || '',
          quantity: item.quantity ?? '1',
          unit_price: item.unit_price ?? '',
          has_igv: item.has_igv !== false,
          sales_quote_item_id: item.sales_quote_item_id || null,
          sales_service_order_item_id: item.sales_service_order_item_id || null,
          product: item.product,
          service: item.service,
        })),
      });
      setUseConvertedQuantityForSubtotal(entity.use_converted_quantity_for_subtotal !== false);
      setLinkedQuotes(
        entity.sales_quote
          ? [{ id: entity.sales_quote.id, code: entity.sales_quote.code || `COT #${entity.sales_quote.id}` }]
          : (entity.sales_quotes || []).map((q) => ({ id: q.id, code: q.code || `COT #${q.id}` })),
      );
      setLinkedServiceOrders(
        (entity.sales_service_orders || []).map((s) => ({
          id: s.id,
          code: s.code || `OSV #${s.id}`,
        })),
      );
      setLinkedContinuousServices(
        (entity.sales_continuous_services || []).map((s) => ({
          id: s.id,
          code: s.code || `SCV #${s.id}`,
        })),
      );
      setLinkedExitGuides(
        (entity.referenced_exit_guides || []).map((g) => ({
          id: g.id,
          code: g.code || `GS #${g.id}`,
        })),
      );
      mergeProducts((entity.items || []).map((item) => item.product).filter(Boolean));
      if (entity.items?.some((item) => item.service)) {
        setServices((prev) => {
          const merged = [...prev];
          entity.items.forEach((item) => {
            if (item.service && !merged.some((s) => s.id === item.service.id)) {
              merged.push(item.service);
            }
          });
          return merged;
        });
      }
      setFormDataLoading(false);
    }
  }, [entity, mergeProducts]);

  const loadFormData = async () => {
    setFormDataLoading(true);
    try {
      const res = await service.getFormData(selectedPointOfSale?.id);
      if (res.success) {
        setClients(res.data?.clients || []);
        setServices(res.data?.services || []);
        setUnits(res.data?.units || []);
        setProjects(res.data?.projects || []);
        applyPosFormDefaults(res.data);
        if (res.data?.sale_price_types?.length) {
          applySalePriceTypes(res.data.sale_price_types);
        } else if (selectedPointOfSale?.id) {
          await loadSalePriceTypes(selectedPointOfSale.id);
        }
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar datos del formulario', type: 'error' });
    } finally {
      if (!entity?.id) setFormDataLoading(false);
    }
  };

  const handleToggleReferenceQuote = (id, checked, entry) => {
    setLinkedQuotes((prev) => {
      if (checked) {
        const next = { id, code: entry?.code || `COT #${id}` };
        if (isServiceOrder) return [next];
        if (prev.some((q) => q.id === id)) return prev;
        return [...prev, next];
      }
      return prev.filter((q) => q.id !== id);
    });
  };

  const handleToggleReferenceServiceOrder = (id, checked, entry) => {
    setLinkedServiceOrders((prev) => {
      if (checked) {
        if (prev.some((s) => s.id === id)) return prev;
        return [...prev, { id, code: entry?.code || `OSV #${id}` }];
      }
      return prev.filter((s) => s.id !== id);
    });
  };

  const handleToggleReferenceContinuousService = (id, checked, entry) => {
    setLinkedContinuousServices((prev) => {
      if (checked) {
        if (prev.some((s) => s.id === id)) return prev;
        return [...prev, { id, code: entry?.code || `SCV #${id}` }];
      }
      return prev.filter((s) => s.id !== id);
    });
  };

  const handleToggleReferenceExitGuide = (id, checked, entry) => {
    setLinkedExitGuides((prev) => {
      if (checked) {
        if (prev.some((g) => g.id === id)) return prev;
        return [...prev, { id, code: entry?.code || `GS #${id}` }];
      }
      return prev.filter((g) => g.id !== id);
    });
    if (!checked) setExitGuideItemsWarning('');
  };

  const formatExitGuideProductLabel = (product, productId) => {
    const name = product?.name?.trim();
    const sku = product?.sku?.trim();
    if (name && sku) return `${sku} — ${name}`;
    if (name) return name;
    if (sku) return sku;
    return `Producto #${productId}`;
  };

  const productHasConfiguredPosPrice = (product) => {
    const gross = getProductPosGrossPrice(product);
    return gross !== null && Number.isFinite(gross);
  };

  const mapQuoteItemToFormLine = (item) => {
    const qty = parseFloat(item.pending_quantity ?? item.quantity ?? 0);
    if (qty <= 0.0001) return null;

    const isService = item.item_type === 'service' || !!item.service_id;
    const shared = {
      unit_of_measure:
        item.unit_of_measure
        || item.service?.unit_of_measure
        || item.product?.unit_of_measure
        || '',
      project_id: item.project_id ? String(item.project_id) : '',
      sales_price_type_id: item.sales_price_type_id ? String(item.sales_price_type_id) : '',
      quantity: String(qty),
      unit_price: item.unit_price ?? '',
      has_igv: item.has_igv !== false,
      sales_quote_item_id: item.id,
    };

    if (isService) {
      if (!item.service_id) return null;
      return mergeProductLineDefaults(
        {
          ...shared,
          item_type: 'service',
          product_id: '',
          service_id: String(item.service_id),
          description: item.notes || item.service?.name || '',
          service: item.service,
        },
        productLineSettings,
      );
    }

    if (!item.product_id) return null;
    return mergeProductLineDefaults(
      {
        ...shared,
        item_type: 'product',
        product_id: String(item.product_id),
        service_id: '',
        product: item.product,
      },
      productLineSettings,
    );
  };

  const mapServiceOrderItemToFormLine = (item) => {
    const qty = parseFloat(item.pending_quantity ?? item.quantity ?? 0);
    if (qty <= 0.0001) return null;
    if (!item.service_id) return null;

    return mergeProductLineDefaults(
      {
        item_type: 'service',
        product_id: '',
        service_id: String(item.service_id),
        description: item.notes || item.service?.name || '',
        unit_of_measure:
          item.unit_of_measure || item.service?.unit_of_measure || '',
        project_id: item.project_id ? String(item.project_id) : '',
        sales_price_type_id: item.sales_price_type_id ? String(item.sales_price_type_id) : '',
        quantity: String(qty),
        unit_price: item.unit_price ?? '',
        has_igv: item.has_igv !== false,
        sales_service_order_item_id: item.id,
        service: item.service,
      },
      productLineSettings,
    );
  };

  /** Cotización u OSV vinculada: rellena cabecera si el formulario aún está vacío. */
  const applyLinkedCommercialHeaderDefaults = (sourceData, prev) => {
    if (!sourceData) return prev;
    const next = { ...prev };
    if (!next.client_id && sourceData.client_id) {
      next.client_id = String(sourceData.client_id);
    }
    if (!next.delivery_date && sourceData.delivery_date) {
      next.delivery_date = formatDateForInput(sourceData.delivery_date);
    }
    if (!next.service_start_date && sourceData.service_start_date) {
      next.service_start_date = formatDateForInput(sourceData.service_start_date);
    }
    if (!next.service_end_date && sourceData.service_end_date) {
      next.service_end_date = formatDateForInput(sourceData.service_end_date);
    }
    return next;
  };
  const applyQuoteHeaderDefaults = applyLinkedCommercialHeaderDefaults;

  const openReferenceModal = async () => {
    setExitGuidesSnapshotAtOpen(linkedExitGuides.map((g) => g.id));
    setQuotesSnapshotAtOpen(linkedQuotes.map((q) => q.id));
    setServiceOrdersSnapshotAtOpen(linkedServiceOrders.map((s) => s.id));
    setIsReferenceModalOpen(true);
    setReferencesLoading(true);
    try {
      const clientId = formData.client_id ? parseInt(formData.client_id, 10) : null;
      const [quotesRes, osvRes, servicesRes, exitGuidesRes] = await Promise.all([
        salesQuoteService.getQuotes({
          point_of_sale_id: selectedPointOfSale?.id,
          status: 'issued',
        }),
        isServiceOrder
          ? Promise.resolve({ success: true, data: [] })
          : salesServiceOrderService.getServiceOrders({
              point_of_sale_id: selectedPointOfSale?.id,
              status: 'issued',
            }),
        isServiceOrder
          ? Promise.resolve({ success: true, data: [] })
          : salesContinuousServiceService.getContinuousServices({
              point_of_sale_id: selectedPointOfSale?.id,
              status: 'open',
            }),
        isServiceOrder
          ? Promise.resolve({ success: true, data: [] })
          : salesOrderService.getReferenceExitGuides({
              client_id: clientId || undefined,
              sales_order_id: entity?.id || undefined,
            }),
      ]);
      let quotes = quotesRes.success ? quotesRes.data || [] : [];
      let serviceOrders = (osvRes.success ? osvRes.data || [] : []).filter(
        (order) => Number(order.fulfillment_percent || 0) < 100,
      );
      let continuous = servicesRes.success ? servicesRes.data || [] : [];
      let exitGuides = exitGuidesRes.success ? exitGuidesRes.data || [] : [];
      if (clientId) {
        quotes = quotes.filter((q) => q.client_id === clientId);
        serviceOrders = serviceOrders.filter((s) => s.client_id === clientId);
        continuous = continuous.filter((s) => s.client_id === clientId);
      }
      setPendingReferenceQuotes(quotes);
      setPendingReferenceServiceOrders(serviceOrders);
      setPendingReferenceContinuousServices(continuous);
      setPendingReferenceExitGuides(exitGuides);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar las referencias', type: 'error' });
      setIsReferenceModalOpen(false);
    } finally {
      setReferencesLoading(false);
    }
  };

  const handleAcceptReferences = async () => {
    setIsReferenceModalOpen(false);

    const previousGuideIds = new Set(exitGuidesSnapshotAtOpen);
    const previousQuoteIds = new Set(quotesSnapshotAtOpen);
    const previousOsvIds = new Set(serviceOrdersSnapshotAtOpen);
    const newlyLinkedGuides = linkedExitGuides.filter((g) => !previousGuideIds.has(g.id));
    const newlyLinkedQuotes = linkedQuotes.filter((q) => !previousQuoteIds.has(q.id));
    const newlyLinkedServiceOrders = linkedServiceOrders.filter((s) => !previousOsvIds.has(s.id));

    if (
      newlyLinkedGuides.length === 0
      && newlyLinkedQuotes.length === 0
      && newlyLinkedServiceOrders.length === 0
    ) {
      setToast({
        isOpen: true,
        message: 'Referencias actualizadas',
        type: 'success',
      });
      return;
    }

    setLoading(true);
    try {
      const existingQuoteItemIds = new Set(
        (formData.items || [])
          .map((item) => item.sales_quote_item_id)
          .filter(Boolean)
          .map((id) => parseInt(id, 10)),
      );
      const existingServiceIds = new Set(
        (formData.items || [])
          .filter((item) => item.item_type === 'service' && item.service_id)
          .map((item) => parseInt(item.service_id, 10)),
      );
      const existingOsvItemIds = new Set(
        (formData.items || [])
          .map((item) => item.sales_service_order_item_id)
          .filter(Boolean)
          .map((id) => parseInt(id, 10)),
      );

      const quoteItemsToAdd = [];
      let skippedQuoteItems = 0;
      const servicesToMerge = [];
      const quoteProductIds = [];
      let firstLinkedQuoteData = null;

      if (newlyLinkedQuotes.length > 0) {
        const quoteResponses = await Promise.all(
          newlyLinkedQuotes.map((quote) => salesQuoteService.getQuote(quote.id)),
        );
        firstLinkedQuoteData = quoteResponses.find((res) => res?.success && res.data)?.data || null;

        quoteResponses.forEach((res) => {
          if (!res?.success || !res.data) return;
          (res.data.items || []).forEach((item) => {
            if (item?.id && existingQuoteItemIds.has(parseInt(item.id, 10))) {
              skippedQuoteItems += 1;
              return;
            }
            const line = mapQuoteItemToFormLine(item);
            if (!line || (isServiceOrder && line.item_type !== 'service')) {
              skippedQuoteItems += 1;
              return;
            }
            if (
              isServiceOrder
              && line.service_id
              && existingServiceIds.has(parseInt(line.service_id, 10))
            ) {
              skippedQuoteItems += 1;
              return;
            }
            if (line.item_type === 'service' && line.service) {
              servicesToMerge.push(line.service);
            }
            if (line.item_type === 'product' && line.product_id) {
              quoteProductIds.push(parseInt(line.product_id, 10));
            }
            quoteItemsToAdd.push(line);
            if (isServiceOrder && line.service_id) {
              existingServiceIds.add(parseInt(line.service_id, 10));
            }
          });
        });

        if (quoteProductIds.length > 0) {
          await ensureByIds(quoteProductIds);
          mergeProducts(
            quoteResponses
              .flatMap((res) => (res?.success ? res.data?.items || [] : []))
              .map((item) => item.product)
              .filter(Boolean),
          );
        }

        if (servicesToMerge.length > 0) {
          setServices((prev) => {
            const merged = [...prev];
            servicesToMerge.forEach((service) => {
              if (!merged.some((entry) => entry.id === service.id)) {
                merged.push(service);
              }
            });
            return merged;
          });
        }
      }

      const osvItemsToAdd = [];
      let skippedOsvItems = 0;
      let firstLinkedOsvData = null;
      if (newlyLinkedServiceOrders.length > 0) {
        const osvResponses = await Promise.all(
          newlyLinkedServiceOrders.map((order) => salesServiceOrderService.getServiceOrder(order.id)),
        );
        const osvServicesToMerge = [];
        osvResponses.forEach((res) => {
          if (!res?.success || !res.data) return;
          if (!firstLinkedOsvData) firstLinkedOsvData = res.data;
          (res.data.items || []).forEach((item) => {
            if (item?.id && existingOsvItemIds.has(parseInt(item.id, 10))) {
              skippedOsvItems += 1;
              return;
            }
            const line = mapServiceOrderItemToFormLine(item);
            if (!line) {
              skippedOsvItems += 1;
              return;
            }
            if (line.service) osvServicesToMerge.push(line.service);
            osvItemsToAdd.push(line);
          });
        });
        if (osvServicesToMerge.length > 0) {
          setServices((prev) => {
            const merged = [...prev];
            osvServicesToMerge.forEach((service) => {
              if (!merged.some((entry) => entry.id === service.id)) {
                merged.push(service);
              }
            });
            return merged;
          });
        }
      }

      const guideItems = [];
      let skippedNonVenta = 0;
      if (newlyLinkedGuides.length > 0) {
        const guideResponses = await Promise.all(
          newlyLinkedGuides.map((guide) => entryGuideService.getEntryGuide(guide.id)),
        );

        guideResponses.forEach((res, index) => {
          if (!res?.success || !res.data) return;
          const guideCode = newlyLinkedGuides[index]?.code || res.data.code || `GS #${res.data.id}`;
          (res.data.items || []).forEach((item) => {
            if (!item?.product_id) return;
            if (String(item.kardex_event || '').toLowerCase() !== 'venta') {
              skippedNonVenta += 1;
              return;
            }
            guideItems.push({ guideCode, item });
          });
        });
      }

      const guideItemsToAdd = [];
      const productsWithoutPrice = [];

      if (guideItems.length > 0) {
        const productIds = [...new Set(guideItems.map(({ item }) => item.product_id))];
        await ensureByIds(productIds);
        mergeProducts(guideItems.map(({ item }) => item.product).filter(Boolean));

        guideItems.forEach(({ guideCode, item }) => {
          const productId = item.product_id;
          const product = findProductById(productId, item.product);
          const label = formatExitGuideProductLabel(product, productId);

          if (!productHasConfiguredPosPrice(product)) {
            if (!productsWithoutPrice.some((entry) => entry.productId === productId)) {
              productsWithoutPrice.push({ productId, label, guideCode });
            }
            return;
          }

          let line = mergeProductLineDefaults(
            {
              item_type: 'product',
              product_id: String(productId),
              service_id: '',
              unit_of_measure: item.unit_of_measure || product?.unit_of_measure || '',
              quantity: String(item.quantity ?? item.converted_quantity ?? '1'),
              unit_price: '',
              has_igv: true,
            },
            productLineSettings,
          );
          line = applyProductListPrice(line, product, formData.igv_rate);

          if (line.unit_price === '' || line.unit_price == null) {
            if (!productsWithoutPrice.some((entry) => entry.productId === productId)) {
              productsWithoutPrice.push({ productId, label, guideCode });
            }
            return;
          }

          guideItemsToAdd.push(line);
        });
      }

      const importedCount = quoteItemsToAdd.length + osvItemsToAdd.length + guideItemsToAdd.length;

      if (importedCount > 0 || firstLinkedQuoteData || firstLinkedOsvData) {
        setFormData((prev) => {
          let next = {
            ...prev,
            items: [...prev.items, ...quoteItemsToAdd, ...osvItemsToAdd, ...guideItemsToAdd],
          };
          if (firstLinkedQuoteData) {
            next = applyLinkedCommercialHeaderDefaults(firstLinkedQuoteData, next);
          }
          if (firstLinkedOsvData) {
            next = applyLinkedCommercialHeaderDefaults(firstLinkedOsvData, next);
          }
          return next;
        });
        setAmountOverride(null);
      }

      if (productsWithoutPrice.length > 0) {
        const names = productsWithoutPrice.map((entry) => entry.label).join(', ');
        const warningMessage = `Los siguientes productos no tienen precio configurado en el punto de venta y no se pueden sacar a la venta: ${names}.`;
        setExitGuideItemsWarning(warningMessage);
        setToast({
          isOpen: true,
          message: warningMessage,
          type: importedCount > 0 ? 'warning' : 'error',
          duration: 12000,
        });
        return;
      }

      setExitGuideItemsWarning('');

      if (importedCount === 0) {
        let infoMessage = 'Referencias actualizadas.';
        if (newlyLinkedQuotes.length > 0 && newlyLinkedGuides.length === 0 && newlyLinkedServiceOrders.length === 0) {
          infoMessage = skippedQuoteItems > 0
            ? 'Referencias actualizadas. La cotización no tiene ítems pendientes para importar.'
            : 'Referencias actualizadas. La cotización no tiene ítems para importar.';
        } else if (newlyLinkedServiceOrders.length > 0 && newlyLinkedGuides.length === 0 && newlyLinkedQuotes.length === 0) {
          infoMessage = skippedOsvItems > 0
            ? 'Referencias actualizadas. La orden de servicio no tiene servicios pendientes para importar.'
            : 'Referencias actualizadas. La orden de servicio no tiene servicios para importar.';
        } else if (newlyLinkedGuides.length > 0) {
          infoMessage = skippedNonVenta > 0
            ? 'Referencias actualizadas. Las guías no tienen productos con evento venta para importar (solo consumo u otros).'
            : 'Referencias actualizadas. Las guías seleccionadas no tienen productos para importar.';
        }
        setToast({ isOpen: true, message: infoMessage, type: 'info' });
        return;
      }

      const serviceCount = [...quoteItemsToAdd, ...osvItemsToAdd].filter((item) => item.item_type === 'service').length;
      const productCount = importedCount - serviceCount;
      const parts = [];
      if (serviceCount > 0) parts.push(`${serviceCount} servicio(s)`);
      if (productCount > 0) parts.push(`${productCount} producto(s)`);
      const skipHint = skippedNonVenta > 0
        ? ` Se omitieron ${skippedNonVenta} ítem(s) de guía sin evento venta.`
        : '';
      setToast({
        isOpen: true,
        message: `Referencias actualizadas. Se importaron ${parts.join(' y ')}.${skipHint}`,
        type: 'success',
      });
    } catch {
      setToast({
        isOpen: true,
        message: 'Error al importar ítems desde las referencias',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedClientName =
    clients.find((c) => String(c.id) === String(formData.client_id))?.name || '';

  const handleClientCreated = async (newClient) => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_client: true });
      if (response.success && response.data) setClients(response.data);
    } catch {
      setClients((prev) => [...prev, newClient]);
    }
    setFormData((prev) => ({ ...prev, client_id: newClient.id.toString() }));
    setToast({ isOpen: true, message: 'Cliente creado y seleccionado', type: 'success' });
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    if (field === 'igv_rate') setAmountOverride(null);
    if (field === 'delivery_date' && value?.trim()) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.delivery_date;
        delete next.service_start_date;
        return next;
      });
    }
    if ((field === 'service_start_date' || field === 'service_end_date') && value?.trim()) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.delivery_date;
        delete next.service_start_date;
        return next;
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    setAmountOverride(null);
    setFormData((prev) => {
      const items = [...prev.items];
      const currentItem = items[index];
      let updatedItem = { ...currentItem };

      if (field === 'quantity') {
        updatedItem.quantity = parseFloat(value) || 0;
      } else if (field === 'unit_price') {
        updatedItem = clearDiscountOfferFields({
          ...updatedItem,
          unit_price: roundPrice(parseFloat(value) || 0),
        });
      } else if (field === 'has_igv') {
        const newHasIgv = value === 'true' || value === true;
        const oldHasIgv = currentItem.has_igv ?? true;
        updatedItem.has_igv = newHasIgv;
        if (isMultiProductOfferLine(currentItem)) {
          updatedItem = applyProductListPrice(
            updatedItem,
            {
              list_price: currentItem.list_unit_price_gross,
              effective_price: currentItem.bundle_unit_price_gross,
            },
            prev.igv_rate,
          );
          updatedItem = copyOfferLineMetadata(currentItem, updatedItem);
        } else if (currentItem.sales_offer_id && currentItem.offer_kind === 'discount') {
          updatedItem = applyProductListPrice(
            updatedItem,
            {
              list_price: currentItem.list_unit_price_gross,
              effective_price: currentItem.offer_unit_price_gross,
            },
            prev.igv_rate,
          );
          updatedItem = copyOfferLineMetadata(currentItem, updatedItem);
        } else if (currentItem.product_id) {
          const product = findProductById(currentItem.product_id);
          const repriced = applyProductListPrice(updatedItem, product, prev.igv_rate);
          const listGross = resolvePosListGrossPrice(product, repriced.sales_price_type_id);
          updatedItem =
            listGross != null && Number.isFinite(listGross)
              ? repriced
              : repriceLineOnIgvToggle(repriced, oldHasIgv, newHasIgv, prev.igv_rate);
        } else if (currentItem.service_id) {
          const svc = findServiceById(currentItem.service_id) || currentItem.service;
          const list = resolveServicePosListPrice(svc, selectedPointOfSale?.id);
          updatedItem = list
            ? applyServiceListPrice(updatedItem, svc, prev.igv_rate)
            : repriceLineOnIgvToggle(updatedItem, oldHasIgv, newHasIgv, prev.igv_rate);
        } else if (oldHasIgv !== newHasIgv) {
          updatedItem.unit_price = adjustNetPriceOnHasIgvChange(currentItem.unit_price, {
            oldHasIgv,
            newHasIgv,
            pricesIncludeIgv: VENTAS_PRICES_INCLUDE_IGV,
            igvRate: prev.igv_rate,
          });
        }
      } else if (field === 'sales_price_type_id') {
        updatedItem.sales_price_type_id = value ? String(value) : '';
        if (
          !isOfferCatalogValue(getItemCatalogSelectionValue(currentItem))
          && !isMultiProductOfferLine(currentItem)
          && currentItem.product_id
        ) {
          updatedItem = clearDiscountOfferFields(updatedItem);
          const product = findProductById(currentItem.product_id);
          updatedItem = applyProductListPrice(updatedItem, product, prev.igv_rate);
        }
      } else {
        updatedItem[field] = value;
      }

      if (field === 'product_id' && value) {
        if (isOfferCatalogValue(value)) {
          return prev;
        }
        updatedItem = clearDiscountOfferFields({ ...updatedItem, product_id: value });
        const product = findProductById(value);
        updatedItem.item_type = 'product';
        updatedItem.service_id = '';
        updatedItem.unit_of_measure = product?.unit_of_measure || '';
        const lineDefaults = getStagingLineDefaults(currentItem);
        if (lineDefaults.sales_price_type_id) {
          updatedItem.sales_price_type_id = lineDefaults.sales_price_type_id;
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) updatedItem.project_id = defaults.project_id;
        if (defaults.has_igv !== undefined) updatedItem.has_igv = defaults.has_igv;
        updatedItem = applyProductListPrice(updatedItem, product, prev.igv_rate);
      }

      if (field === 'service_id' && value) {
        const service = findServiceById(value);
        updatedItem.item_type = 'service';
        updatedItem.product_id = '';
        updatedItem.unit_of_measure = service?.unit_of_measure || '';
        if (service?.name) {
          updatedItem.description = service.name;
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) updatedItem.project_id = defaults.project_id;
        if (productsIgvEnabled && service) {
          const oldHasIgv = currentItem.has_igv ?? true;
          const newHasIgv = service.is_taxable !== false;
          updatedItem.has_igv = newHasIgv;
          if (oldHasIgv !== newHasIgv && !resolveServicePosListPrice(service, selectedPointOfSale?.id)) {
            updatedItem.unit_price = adjustNetPriceOnHasIgvChange(currentItem.unit_price, {
              oldHasIgv,
              newHasIgv,
              pricesIncludeIgv: VENTAS_PRICES_INCLUDE_IGV,
              igvRate: prev.igv_rate,
            });
          }
        }
        if (service) {
          updatedItem = applyServiceListPrice(updatedItem, service, prev.igv_rate);
        }
      }

      items[index] = updatedItem;
      return { ...prev, items };
    });
  };

  const handleStagingChange = (field, value) => {
    setStagingError('');
    setStagingItem((prev) => {
      let updated = { ...prev };
      if (field === 'quantity') {
        updated.quantity = parseFloat(value) || 0;
      } else if (field === 'unit_price') {
        updated = clearDiscountOfferFields({
          ...updated,
          unit_price: roundPrice(parseFloat(value) || 0),
        });
      } else if (field === 'has_igv') {
        const newHasIgv = value === 'true' || value === true;
        const oldHasIgv = prev.has_igv ?? true;
        updated.has_igv = newHasIgv;
        if (oldHasIgv !== newHasIgv) {
          if (isMultiProductOfferLine(prev)) {
            updated = applyProductListPrice(
              updated,
              {
                list_price: prev.list_unit_price_gross,
                effective_price: prev.bundle_unit_price_gross,
              },
              formData.igv_rate,
            );
            updated = copyOfferLineMetadata(prev, updated);
          } else if (prev.sales_offer_id && prev.offer_kind === 'discount') {
            updated = applyProductListPrice(
              updated,
              {
                list_price: prev.list_unit_price_gross,
                effective_price: prev.offer_unit_price_gross,
              },
              formData.igv_rate,
            );
            updated = copyOfferLineMetadata(prev, updated);
          } else {
            const product = prev.product_id ? findProductById(prev.product_id) : null;
            const listGross = product
              ? resolvePosListGrossPrice(product, updated.sales_price_type_id)
              : null;
            if (listGross != null && Number.isFinite(listGross)) {
              updated = applyProductListPrice(updated, product, formData.igv_rate);
            } else {
              updated = repriceLineOnIgvToggle(updated, oldHasIgv, newHasIgv, formData.igv_rate);
            }
          }
        }
      } else if (field === 'sales_price_type_id') {
        updated.sales_price_type_id = value ? String(value) : '';
        if (!isMultiProductOfferLine(prev) && prev.product_id) {
          updated = clearDiscountOfferFields(updated);
          const product = findProductById(prev.product_id);
          updated = applyProductListPrice(updated, product, formData.igv_rate);
        }
      } else if (field === 'product_id') {
        if (isOfferCatalogValue(value)) {
          return prev;
        }
        if (!value) {
          return clearDiscountOfferFields({
            ...updated,
            product_id: '',
            unit_price: '',
            unit_of_measure: '',
          });
        }
        updated = clearDiscountOfferFields({ ...updated, product_id: String(value) });
        const product = findProductById(value);
        updated.item_type = 'product';
        updated.service_id = '';
        updated.unit_of_measure = product?.unit_of_measure || '';
        const lineDefaults = getStagingLineDefaults(prev);
        if (lineDefaults.sales_price_type_id) {
          updated.sales_price_type_id = lineDefaults.sales_price_type_id;
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) updated.project_id = defaults.project_id;
        if (defaults.has_igv !== undefined) updated.has_igv = defaults.has_igv;
        updated = applyProductListPrice(updated, product, formData.igv_rate);
      } else {
        updated[field] = value;
      }
      return updated;
    });
  };

  const handleQuickAddItem = () => {
    const lineToAdd = resolveStagingLineForAdd();
    if (!lineToAdd.product_id) {
      setStagingError('Seleccione un producto');
      return;
    }
    if (!lineToAdd.quantity || lineToAdd.quantity <= 0) {
      setStagingError('La cantidad debe ser mayor a 0');
      return;
    }
    if (lineToAdd.unit_price === '' || lineToAdd.unit_price < 0) {
      setStagingError('El precio unitario es requerido');
      return;
    }
    if (
      hasSaleTypes
      && !isMultiProductOfferLine(lineToAdd)
      && !lineToAdd.sales_price_type_id
    ) {
      setStagingError('Seleccione el tipo de venta');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        copyOfferLineMetadata(lineToAdd, {
          ...lineToAdd,
          item_type: 'product',
          product_id: String(lineToAdd.product_id),
        }),
      ],
    }));
    setStagingSelectionId('');
    setStagingItem(emptyStagingWithDefaults(emptyPurchaseOrderStagingItem, productLineSettings));
    setStagingError('');
    setAmountOverride(null);
    window.setTimeout(() => stagingProductSelectorRef.current?.focus?.(), 0);
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        mergeProductLineDefaults(
          {
            item_type: 'product',
            product_id: '',
            service_id: '',
            unit_of_measure: '',
            project_id: '',
            sales_price_type_id: defaultLineSaleTypeId || '',
            quantity: '1',
            unit_price: '',
            has_igv: true,
          },
          productLineSettings,
        ),
      ],
    }));
  };

  const handleAddService = () => {
    setAmountOverride(null);
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, mergeProductLineDefaults(emptyVentasServiceItem(), productLineSettings)],
    }));
  };

  const removeItem = (index) => {
    setAmountOverride(null);
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleConfirmImportPreview = (confirmedRows) => {
    const source = confirmedRows || importPreview?.items || [];
    if (!source.length) {
      setImportPreview(null);
      return;
    }
    const toAdd = source.map(stripImportMeta).map((item) => ({
      ...item,
      item_type: 'product',
      product_id: String(item.product_id),
      service_id: '',
      project_id: item.project_id ? String(item.project_id) : '',
    }));
    setFormData((prev) => ({ ...prev, items: [...prev.items, ...toAdd] }));
    setImportPreview(null);
    setAmountOverride(null);
    setToast({ isOpen: true, message: `Se añadieron ${toAdd.length} fila(s).`, type: 'info' });
  };

  const handleItemsImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportingItems(true);
    try {
      const { items: parsed, errors: importErrors, warnings } = await parsePurchaseOrderItemsExcel(file, {
        products,
        units,
        projects,
        productConversionsEnabled,
      });
      if (parsed.length === 0 && !importErrors.length) {
        setToast({ isOpen: true, message: 'No se importó ninguna fila con datos.', type: 'warning' });
        return;
      }
      setImportPreview({
        fileName: file.name,
        items: parsed,
        errors: importErrors || [],
        warnings: warnings || [],
      });
    } catch (err) {
      setToast({ isOpen: true, message: err?.message || 'Error al leer Excel', type: 'error' });
    } finally {
      setImportingItems(false);
    }
  };

  const getLineSubtotal = (item) => {
    if (item.item_type === 'service') {
      const qty = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      return Math.round(qty * unitPrice * 100) / 100;
    }
    const product = findProductById(item.product_id, item.product);
    return getItemLineSubtotal({
      product,
      unitCode: item.unit_of_measure,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      productConversionsEnabled,
      useConvertedQuantityForSubtotal,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.client_id) newErrors.client_id = 'El cliente es requerido';
    if (!formData.issue_date) newErrors.issue_date = 'La fecha es requerida';
    if (!selectedPointOfSale?.id) newErrors.point_of_sale = 'Seleccione un punto de venta';
    if (!formData.items.length) newErrors.items = 'Agregue al menos un ítem';
    if (isServiceOrder && !formData.items.some((item) => item.item_type === 'service')) {
      newErrors.items = 'La orden de servicio debe incluir al menos un servicio';
    }
    if (!isQuoteLike) {
      if (!formData.document_type) newErrors.document_type = 'Seleccione el tipo de comprobante';
      const isManualNumbering = formData.numbering_mode === 'manual';
      if (
        !entity?.id
        && !isManualNumbering
        && formData.document_type
        && !posSeriesByType?.[formData.document_type]
      ) {
        newErrors.document_type =
          'El punto de venta no tiene serie configurada para este comprobante. Configúrela en Ventas → Serie por punto de venta o use numeración manual.';
      }
      if (!entity?.id && isManualNumbering) {
        if (!String(formData.series || '').trim()) {
          newErrors.series = 'Ingrese la serie del comprobante';
        }
        if (!String(formData.number || '').trim()) {
          newErrors.number = 'Ingrese el número del comprobante';
        }
      }
      if (formData.payment_condition_type === 'cash' && !formData.payment_method) {
        newErrors.payment_method = 'Indique el medio de pago';
      }
      if (
        ventasPaymentMethodNeedsOperation(formData.payment_method) &&
        !String(formData.operation_number || '').trim()
      ) {
        newErrors.operation_number = 'Indique el número de operación';
      }
      if (formData.issue_date && closedShiftDates.includes(formData.issue_date)) {
        newErrors.issue_date = 'El turno de este día ya fue cerrado. No se pueden registrar ventas.';
      }
    }

    const hasDeliveryDate = formData.delivery_date?.trim();
    const hasServiceDates =
      formData.service_start_date?.trim() && formData.service_end_date?.trim();
    if (formData.items.length && !hasDeliveryDate && !hasServiceDates) {
      newErrors.delivery_date = 'Debe ingresar la fecha de entrega o las fechas de servicio';
      newErrors.service_start_date = 'Debe ingresar la fecha de entrega o las fechas de servicio';
    }

    formData.items.forEach((item, idx) => {
      const isService = item.item_type === 'service';
      if (isService) {
        if (!item.service_id) newErrors[`item_${idx}_service`] = 'Servicio requerido';
        if (!String(item.description || '').trim()) {
          newErrors[`item_${idx}_description`] = 'Descripción requerida';
        }
      } else if (!item.product_id) {
        newErrors[`item_${idx}_product`] = 'Producto requerido';
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`item_${idx}_quantity`] = 'Cantidad inválida';
      }
      if (item.unit_price === '' || parseFloat(item.unit_price) < 0) {
        newErrors[`item_${idx}_price`] = 'Precio requerido';
      }
      if (
        hasSaleTypes
        && item.item_type !== 'service'
        && !isMultiProductOfferLine(item)
        && !item.sales_price_type_id
      ) {
        newErrors[`item_${idx}_sale_type`] = 'Tipo de venta requerido';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (loading) return;
    if (!validateForm()) {
      setToast({ isOpen: true, message: 'Complete los campos requeridos', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const totals = displayTotals;
      const payload = {
        client_id: parseInt(formData.client_id, 10),
        point_of_sale_id: selectedPointOfSale.id,
        issue_date: formData.issue_date,
        currency: formData.currency || 'PEN',
        igv_rate: parseIgvRate(formData.igv_rate),
        notes: formData.notes || null,
        delivery_date: formData.delivery_date || null,
        service_start_date: formData.service_start_date || null,
        service_end_date: formData.service_end_date || null,
        payment_condition_type: formData.payment_condition_type,
        payment_condition_days:
          formData.payment_condition_type === 'days'
            ? parseInt(formData.payment_condition_days, 10)
            : null,
        payment_condition_date:
          formData.payment_condition_type === 'date' ? formData.payment_condition_date || null : null,
        use_converted_quantity_for_subtotal: useConvertedQuantityForSubtotal,
        base_imponible: totals.baseImponibleNeta ?? totals.baseImponible,
        igv_amount: totals.igv,
        no_gravados: totals.noGravados,
        total_amount: totals.total,
        items: (isServiceOrder
          ? formData.items.filter((item) => item.item_type === 'service')
          : formData.items
        ).map((item) => {
          const isService = item.item_type === 'service';
          const subtotal = Math.round(getLineSubtotal(item) * 100) / 100;
          return {
            item_type: isService ? 'service' : 'product',
            product_id: !isService && item.product_id ? parseInt(item.product_id, 10) : null,
            service_id: isService && item.service_id ? parseInt(item.service_id, 10) : null,
            unit_of_measure: item.unit_of_measure || null,
            sales_price_type_id:
              !isService && item.sales_price_type_id
                ? parseInt(item.sales_price_type_id, 10)
                : null,
            project_id: item.project_id ? parseInt(item.project_id, 10) : null,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
            subtotal,
          has_igv: item.has_igv !== false,
            ...(isService ? { description: String(item.description || '').trim() } : {}),
            ...(!isQuote && item.sales_quote_item_id
              ? { sales_quote_item_id: parseInt(item.sales_quote_item_id, 10) }
              : {}),
            ...(!isQuote && !isServiceOrder && item.sales_service_order_item_id
              ? { sales_service_order_item_id: parseInt(item.sales_service_order_item_id, 10) }
              : {}),
          };
        }),
      };
      if (isQuote) {
        payload.valid_until = formData.valid_until || null;
      } else if (isServiceOrder) {
        payload.sales_quote_id = linkedQuotes[0]?.id || null;
      } else {
        payload.document_type = formData.document_type || null;
        if (!entity?.id) {
          payload.numbering_mode = formData.numbering_mode || 'auto';
          if (formData.numbering_mode === 'manual') {
            payload.series = String(formData.series || '').trim();
            payload.number = String(formData.number || '').trim();
          }
        }
        payload.payment_method = formData.payment_method || null;
        payload.operation_number = ventasPaymentMethodNeedsOperation(formData.payment_method)
          ? String(formData.operation_number || '').trim() || null
          : null;
        payload.sales_quote_ids = linkedQuotes.map((q) => q.id);
        payload.sales_service_order_ids = linkedServiceOrders.map((s) => s.id);
        payload.sales_continuous_service_ids = linkedContinuousServices.map((s) => s.id);
        payload.exit_guide_ids = linkedExitGuides.map((g) => g.id);
      }

      let res;
      if (entity?.id) {
        res = isQuote
          ? await salesQuoteService.updateQuote(entity.id, payload)
          : isServiceOrder
            ? await salesServiceOrderService.updateServiceOrder(entity.id, payload)
          : await salesOrderService.updateOrder(entity.id, payload);
      } else {
        res = isQuote
          ? await salesQuoteService.createQuote(payload)
          : isServiceOrder
            ? await salesServiceOrderService.createServiceOrder(payload)
          : await salesOrderService.createOrder(payload);
      }

      if (res.success) {
        if (onSuccess) {
          onSuccess(res.data);
        } else {
        setToast({
          isOpen: true,
          message: res.message || (entity?.id ? 'Actualizado' : 'Registrado'),
          type: 'success',
        });
        }
      } else {
        setErrors(res.errors || {});
        setToast({ isOpen: true, message: res.message || 'Error al guardar', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al guardar',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) =>
    new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: formData.currency === 'USD' ? 'USD' : 'PEN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(roundMoney(amount || 0));

  if (formDataLoading) {
    return (
      <FormLoadingSpinner
        message="Cargando formulario…"
        hint={isQuoteLike ? 'Preparando clientes y servicios' : 'Preparando datos de venta'}
      />
    );
  }

  const showConversionToggle =
    productConversionsEnabled &&
    formData.items.some(
      (item) =>
        item.item_type !== 'service' &&
        itemHasCustomConversion({
          product: findProductById(item.product_id, item.product),
          unitCode: item.unit_of_measure,
          productConversionsEnabled,
        }),
    );

  return (
    <>
    <form
      onSubmit={handleSubmit}
      onKeyDown={handlePreventEnterFormSubmit}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm h-full">
          <div className="flex justify-between items-center mb-5 pb-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Información Básica
            </h3>
            {!isQuote && !readOnly && (
              <button
                type="button"
                onClick={openReferenceModal}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                Seleccionar referencias
              </button>
            )}
          </div>

          {!isQuote && (linkedQuotes.length > 0 || linkedServiceOrders.length > 0 || linkedContinuousServices.length > 0 || linkedExitGuides.length > 0) && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Referencias:</span>
              {linkedQuotes.map((quote) => (
                <span
                  key={`quote-${quote.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-mono text-teal-800"
                >
                  {quote.code}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleToggleReferenceQuote(quote.id, false)}
                      className="text-teal-600 hover:text-teal-900"
                      aria-label={`Quitar ${quote.code}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!isServiceOrder && linkedServiceOrders.map((order) => (
                <span
                  key={`osv-${order.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-mono text-indigo-900"
                >
                  {order.code}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleToggleReferenceServiceOrder(order.id, false)}
                      className="text-indigo-700 hover:text-indigo-900"
                      aria-label={`Quitar ${order.code}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!isServiceOrder && linkedContinuousServices.map((service) => (
                <span
                  key={`scv-${service.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-cyan-100 px-2.5 py-0.5 text-xs font-mono text-cyan-900"
                >
                  {service.code}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleToggleReferenceContinuousService(service.id, false)}
                      className="text-cyan-700 hover:text-cyan-900"
                      aria-label={`Quitar ${service.code}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!isServiceOrder && linkedExitGuides.map((guide) => (
                <span
                  key={`gs-${guide.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-mono text-amber-900"
                >
                  {guide.code}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleToggleReferenceExitGuide(guide.id, false)}
                      className="text-amber-700 hover:text-amber-900"
                      aria-label={`Quitar ${guide.code}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {!isQuote && !isServiceOrder && exitGuideItemsWarning ? (
            <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {exitGuideItemsWarning}
            </div>
          ) : null}

          <div className="space-y-5">
        <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
            Cliente <span className="text-red-500">*</span>
          </label>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setIsCreateClientModalOpen(true)}
                    className="text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                  >
                    + Nuevo
                  </button>
                )}
              </div>
          <SupplierSelector
            value={formData.client_id}
            onChange={(value) => handleFieldChange('client_id', value)}
            suppliers={clients}
            disabled={readOnly}
            error={errors.client_id}
                placeholder="Buscar cliente por nombre, código o documento..."
          />
          {errors.client_id && <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>}
        </div>

            <div>
              <IgvRateSelect
                value={formData.igv_rate}
                onChange={(value) => handleFieldChange('igv_rate', value)}
                disabled={readOnly}
                showLabel
                label="Tasa de IGV"
              />
            </div>

            {!isQuoteLike && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de comprobante <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.document_type}
                  onChange={(e) => handleFieldChange('document_type', e.target.value)}
                  disabled={readOnly}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    errors.document_type ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                  }`}
                >
                  {VENTAS_DOCUMENT_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.document_type && (
                  <p className="mt-1 text-sm text-red-600">{errors.document_type}</p>
                )}
                {!isQuoteLike && !entity?.id ? (
                  <div className="mt-3 space-y-3">
                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-2">Numeración</span>
                      <div className="flex flex-wrap gap-4">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="numbering_mode"
                            value="auto"
                            checked={formData.numbering_mode !== 'manual'}
                            onChange={() => handleFieldChange('numbering_mode', 'auto')}
                            disabled={readOnly}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          Autonumeración
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="numbering_mode"
                            value="manual"
                            checked={formData.numbering_mode === 'manual'}
                            onChange={() => handleFieldChange('numbering_mode', 'manual')}
                            disabled={readOnly}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          Numeración manual
                        </label>
                      </div>
                    </div>

                    {formData.numbering_mode === 'manual' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Serie <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.series}
                            onChange={(e) => handleFieldChange('series', e.target.value.toUpperCase())}
                            disabled={readOnly}
                            placeholder="Ej. F001"
                            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono ${
                              errors.series ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors.series && (
                            <p className="mt-1 text-sm text-red-600">{errors.series}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Número <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.number}
                            onChange={(e) => handleFieldChange('number', e.target.value)}
                            disabled={readOnly}
                            placeholder="Ej. 00001234"
                            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono ${
                              errors.number ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors.number && (
                            <p className="mt-1 text-sm text-red-600">{errors.number}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {formData.document_type && posSeriesByType?.[formData.document_type] ? (
                          <p className="text-sm text-gray-500">
                            Serie configurada:{' '}
                            <span className="font-mono">{posSeriesByType[formData.document_type]}</span>
                            {peekedSeriesNumber?.number ? (
                              <>
                                {' '}
                                — siguiente:{' '}
                                <span className="font-mono">
                                  {peekedSeriesNumber.series}-{peekedSeriesNumber.number}
                                </span>
                              </>
                            ) : (
                              ' (se asignará al guardar)'
                            )}
                          </p>
                        ) : formData.document_type ? (
                          <p className="text-sm text-amber-700">
                            Configure la serie en{' '}
                            <Link to="/ventas/pos-series-config" className="text-teal-700 hover:underline">
                              Serie por punto de venta
                            </Link>{' '}
                            o use numeración manual.
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
                {!isQuoteLike && entity?.series && entity?.number ? (
                  <p className="mt-1 text-sm text-gray-500">
                    Comprobante asignado:{' '}
                    <span className="font-mono">
                      {entity.series}-{entity.number}
                    </span>
                  </p>
                ) : null}
              </div>
            )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fecha de emisión <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.issue_date}
            onChange={(e) => handleFieldChange('issue_date', e.target.value)}
            disabled={readOnly}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.issue_date ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
            }`}
          />
          {errors.issue_date && <p className="mt-1 text-sm text-red-600">{errors.issue_date}</p>}
              {!isQuoteLike && formData.issue_date && closedShiftDates.includes(formData.issue_date) ? (
                <p className="mt-1 text-sm text-amber-700">
                  El turno de esta fecha ya fue cerrado. No puede registrar ni modificar ventas de este día.
                </p>
              ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Moneda del comprobante <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.currency || 'PEN'}
            onChange={(e) => handleFieldChange('currency', e.target.value)}
            disabled={readOnly}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="PEN">Soles (PEN)</option>
            <option value="USD">Dólares (USD)</option>
          </select>
        </div>

        {isQuote && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Válida hasta</label>
            <input
              type="date"
              value={formData.valid_until}
              onChange={(e) => handleFieldChange('valid_until', e.target.value)}
              disabled={readOnly}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        )}
        </div>
      </div>

        <PaymentConditionSection
          issueDate={formData.issue_date}
          paymentConditionType={formData.payment_condition_type}
          paymentConditionDays={formData.payment_condition_days}
          paymentConditionDate={formData.payment_condition_date}
          paymentMethod={formData.payment_method}
          operationNumber={formData.operation_number}
          onTypeChange={(value) => handleFieldChange('payment_condition_type', value)}
          onDaysChange={(value) => handleFieldChange('payment_condition_days', value)}
          onDateChange={(value) => handleFieldChange('payment_condition_date', value)}
          onPaymentMethodChange={(value) => {
            setFormData((prev) => ({
              ...prev,
              payment_method: value,
              operation_number: ventasPaymentMethodNeedsOperation(value) ? prev.operation_number : '',
            }));
            setErrors((prev) => {
              const next = { ...prev };
              delete next.payment_method;
              if (!ventasPaymentMethodNeedsOperation(value)) delete next.operation_number;
              return next;
            });
          }}
          onOperationNumberChange={(value) => handleFieldChange('operation_number', value)}
          showPaymentMethod={!isQuoteLike}
          readOnly={readOnly}
          className="mb-0"
        />
      </div>
      {errors.payment_method && (
        <p className="mb-4 text-sm text-red-600">{errors.payment_method}</p>
      )}
      {errors.operation_number && (
        <p className="mb-4 text-sm text-red-600">{errors.operation_number}</p>
      )}

      {errors.point_of_sale && <p className="mb-4 text-sm text-red-600">{errors.point_of_sale}</p>}

      <div className="border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex flex-col gap-3 mb-4 pb-3 border-b border-gray-200">
          <div className="flex flex-wrap justify-between items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-800">Ítems</h3>
        {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                {!isServiceOrder && !quickProductLineEnabled && (
          <button
            type="button"
            onClick={addItem}
            className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
          >
                    + Producto
          </button>
                )}
                <button
                  type="button"
                  onClick={handleAddService}
                  className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  + Servicio
                </button>
                {!isServiceOrder && (
                  <>
                <button
                  type="button"
                  onClick={() => downloadPurchaseOrderItemsTemplate()}
                  className="px-3 py-2 border border-gray-300 bg-white text-gray-800 rounded-md hover:bg-gray-50 text-sm"
                >
                  Descargar plantilla
                </button>
                <input
                  ref={itemsImportFileRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={handleItemsImportFile}
                />
                <button
                  type="button"
                  onClick={() => itemsImportFileRef.current?.click()}
                  disabled={importingItems || readOnly}
                  className="px-3 py-2 border border-teal-300 bg-teal-50 text-teal-900 rounded-md hover:bg-teal-100 text-sm disabled:opacity-50"
                >
                  {importingItems ? 'Importando…' : 'Importar Excel'}
                </button>
                  </>
                )}
              </div>
            )}
          </div>
          {!readOnly && showConversionToggle && (
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <SubtotalQuantityModeToggle
                value={useConvertedQuantityForSubtotal}
                onChange={setUseConvertedQuantityForSubtotal}
              />
            </div>
        )}
      </div>

      {errors.items && <p className="mb-2 text-sm text-red-600">{errors.items}</p>}

        {!readOnly && !isServiceOrder && quickProductLineEnabled && (
          <QuickProductLineBar
            onAdd={handleQuickAddItem}
            error={stagingError}
            rowClassName={PO_ITEM_ROW}
            defaultsSlot={
              showProductLineDefaults ? (
                <ProductLineDefaultsBar
                  productsProjectEnabled={showProjectSelector}
                  productsIgvEnabled={productsIgvEnabled}
                  projects={projects}
                  defaultProjectId={defaultLineProjectId}
                  onDefaultProjectIdChange={setDefaultLineProjectId}
                  defaultHasIgv={defaultLineHasIgv}
                  onDefaultHasIgvChange={setDefaultLineHasIgv}
                  salePriceTypes={salePriceTypes}
                  defaultSaleTypeId={defaultLineSaleTypeId}
                  onDefaultSaleTypeIdChange={setDefaultLineSaleTypeId}
                  className="mb-0"
                />
              ) : null
            }
          >
            <LineItemField label="Producto" required className={productFieldClass}>
              <VentasCatalogSelector
                ref={stagingProductSelectorRef}
                value={stagingCatalogValue}
                onChange={(value) => {
                  if (!value) {
                    clearStagingProductSelection();
                    return;
                  }
                  if (isOfferCatalogValue(value)) {
                    setStagingSelectionId(String(value));
                    const offer = getCatalogEntry(value);
                    if (offer?.catalog_type === 'offer') {
                      applyOfferToStaging(offer);
                    }
                    return;
                  }
                  setStagingSelectionId('');
                  handleStagingChange('product_id', value);
                }}
                onProductPicked={(entry) => {
                  if (entry?.catalog_type === 'offer') {
                    applyOfferToStaging(entry);
                    return;
                  }
                  if (entry?.id) {
                    mergeProducts([entry]);
                  }
                }}
                skipEnsureForValue={isOfferCatalogValue}
                {...productSelectorAsyncProps}
                compact
              />
            </LineItemField>
            {hasSaleTypes && !isMultiProductOfferLine(stagingItem) && (
              <LineItemField label="Tipo de venta" required className={POIC.saleType}>
                <select
                  value={stagingItem.sales_price_type_id || ''}
                  onChange={(e) => handleStagingChange('sales_price_type_id', e.target.value)}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Seleccionar</option>
                  {salePriceTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                      {type.is_default ? ' (predeterminado)' : ''}
                    </option>
                  ))}
                </select>
              </LineItemField>
            )}
            {showProjectSelector && (
              <LineItemField label="Proyecto" className={POIC.project}>
                <select
                  value={stagingItem.project_id || ''}
                  onChange={(e) => handleStagingChange('project_id', e.target.value)}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Seleccionar</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} - {p.name}
                    </option>
                  ))}
                </select>
              </LineItemField>
            )}
            <LineItemField label="Cant." required className={POIC.qty}
              hint={
                stagingItem.product_id && !isMultiProductOfferLine(stagingItem) ? (
                  <QuantityConversionHint
                    product={findProductById(stagingItem.product_id)}
                    unitCode={stagingItem.unit_of_measure}
                    quantity={stagingItem.quantity}
                  />
                ) : null
              }
            >
              <QuantityTextInput
                value={stagingItem.quantity}
                onChange={(qty) => handleStagingChange('quantity', qty)}
                min={0.001}
                className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </LineItemField>
            <LineItemField
              label="P. unit."
              required
              className={POIC.price}
              hint={
                <PriceIgvHint
                  netPrice={stagingItem.unit_price}
                  igvRate={formData.igv_rate}
                  hasIgv={stagingItem.has_igv ?? true}
                  pricesIncludeIgv={VENTAS_PRICES_INCLUDE_IGV}
                />
              }
            >
              <div className="relative w-full">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                <UnitPriceTextInput
                  netPrice={stagingItem.unit_price}
                  onNetPriceChange={(net) => handleStagingChange('unit_price', net)}
                  pricesIncludeIgv={VENTAS_PRICES_INCLUDE_IGV}
                  hasIgv={stagingItem.has_igv ?? true}
                  igvRate={formData.igv_rate}
                  className="w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
              </div>
            </LineItemField>
            <LineItemField label="IGV" className={POIC.igv}>
              <select
                value={(stagingItem.has_igv ?? true) ? 'true' : 'false'}
                onChange={(e) => handleStagingChange('has_igv', e.target.value)}
                className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              >
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </LineItemField>
          </QuickProductLineBar>
        )}

      {formData.items.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            {isServiceOrder
              ? 'No hay servicios. Agregue un servicio o impórtelo desde una cotización.'
              : 'No hay ítems. Agregue productos o servicios manualmente o importe productos desde Excel.'}
        </p>
      ) : (
          <div className="space-y-3">
              {formData.items.map((item, index) => {
              const isService = item.item_type === 'service';
              const product = !isService ? findProductById(item.product_id, item.product) : null;
              const service = isService ? findServiceById(item.service_id) || item.service : null;
              const line = calcVentasLine(item, igvRate, {
                product,
                productConversionsEnabled,
                useConvertedQuantityForSubtotal,
              });

                return (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className={PO_ITEM_ROW}>
                    {isService ? (
                      <>
                        <LineItemField label="Servicio" required className={POIC.service}>
                          <ServiceSelector
                            value={item.service_id}
                            onChange={(value) => handleItemChange(index, 'service_id', value)}
                            services={services}
                            disabled={readOnly}
                            error={errors[`item_${index}_service`]}
                            compact
                          />
                        </LineItemField>
                        <LineItemField label="Descr." required className={POIC.description}>
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            disabled={readOnly}
                            placeholder="Descripción del servicio"
                            className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 ${
                              errors[`item_${index}_description`]
                                ? 'border-red-300 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                            }`}
                          />
                        </LineItemField>
                      </>
                    ) : (
                      <LineItemField label="Producto" required className={productFieldClass}>
                        <VentasCatalogSelector
                          value={getItemCatalogSelectionValue(item)}
                          onChange={(value) => {
                            if (isOfferCatalogValue(value)) return;
                            handleItemChange(index, 'product_id', value);
                          }}
                          onProductPicked={(entry) => {
                            if (entry?.catalog_type === 'offer') {
                              applyOfferToItemRow(index, entry);
                            }
                          }}
                          skipEnsureForValue={isOfferCatalogValue}
                        disabled={readOnly}
                        error={errors[`item_${index}_product`]}
                        compact
                        {...productSelectorAsyncProps}
                      />
                      </LineItemField>
                    )}
                    {hasSaleTypes && !isMultiProductOfferLine(item) && (
                      <LineItemField label="Tipo de venta" required className={POIC.saleType}>
                        <select
                          value={item.sales_price_type_id || ''}
                          onChange={(e) => handleItemChange(index, 'sales_price_type_id', e.target.value)}
                          disabled={readOnly}
                          className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 ${
                            errors[`item_${index}_sale_type`]
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                          }`}
                        >
                          <option value="">Seleccionar</option>
                          {salePriceTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                              {type.is_default ? ' (predeterminado)' : ''}
                            </option>
                          ))}
                        </select>
                      </LineItemField>
                    )}
                    {showProjectSelector && (
                      <LineItemField label="Proyecto" className={POIC.project}>
                        <select
                          value={item.project_id || ''}
                          onChange={(e) => handleItemChange(index, 'project_id', e.target.value)}
                          disabled={readOnly}
                          className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        >
                          <option value="">Seleccionar</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code} - {p.name}
                            </option>
                          ))}
                        </select>
                      </LineItemField>
                    )}
                    <LineItemField
                      label="Cant."
                      required
                      className={POIC.qty}
                      hint={
                        !isService ? (
                          <QuantityConversionHint
                            product={product}
                            unitCode={item.unit_of_measure}
                            quantity={item.quantity}
                          />
                        ) : service?.unit_of_measure ? (
                          <span className="text-gray-500">{service.unit_of_measure}</span>
                        ) : null
                      }
                    >
                      <QuantityTextInput
                        value={item.quantity}
                        onChange={(qty) => handleItemChange(index, 'quantity', qty)}
                        min={0.001}
                        disabled={readOnly}
                        className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                      />
                    </LineItemField>
                    <LineItemField
                      label="P. unit."
                      required
                      className={POIC.price}
                      hint={
                        <PriceIgvHint
                          netPrice={item.unit_price}
                          igvRate={formData.igv_rate}
                          hasIgv={item.has_igv ?? true}
                          pricesIncludeIgv={VENTAS_PRICES_INCLUDE_IGV}
                        />
                      }
                    >
                      <div className="relative w-full">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                        <UnitPriceTextInput
                          netPrice={item.unit_price}
                          onNetPriceChange={(net) => handleItemChange(index, 'unit_price', net)}
                          pricesIncludeIgv={VENTAS_PRICES_INCLUDE_IGV}
                          hasIgv={item.has_igv ?? true}
                          igvRate={formData.igv_rate}
                        disabled={readOnly}
                          className="w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                    </LineItemField>
                    <LineItemField label="IGV" className={POIC.igv}>
                      <select
                        value={(item.has_igv ?? true) ? 'true' : 'false'}
                        onChange={(e) => handleItemChange(index, 'has_igv', e.target.value)}
                        disabled={readOnly}
                        className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                      >
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                    </LineItemField>
                    <LineItemField
                      label="Subtotal"
                      className={POIC.subtotal}
                      hint={
                        <PriceIgvHint
                          netPrice={line.subtotal}
                          igvRate={formData.igv_rate}
                          hasIgv={item.has_igv ?? true}
                          alwaysConIgv
                        />
                      }
                    >
                      <div className="w-full h-[34px] px-2 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md text-gray-700 font-medium flex items-center">
                        S/{' '}
                        {parseFloat(line.subtotal).toLocaleString('es-PE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </LineItemField>
                    {!readOnly && (
                      <LineItemField hideLabel className={POIC.actions}>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="h-[34px] w-full flex items-center justify-center bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                          title="Quitar ítem"
                        >
                          ✕
                        </button>
                      </LineItemField>
                    )}
                  </div>
                </div>
                );
              })}
        </div>
      )}
      </div>

      <div className="border-t border-gray-200 pt-4 mb-6">
        <div className="flex justify-end">
          <div className="w-full md:w-80 space-y-2 text-sm">
            {!isEditingAmounts ? (
              <>
                <div className="flex justify-between">
            <span className="text-gray-600">Base imponible:</span>
                  <span>{formatMoney(displayTotals.baseImponible)}</span>
          </div>
                {(displayTotals.descuentoGravado ?? 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Descuento (gravado):</span>
                    <span>- {formatMoney(displayTotals.descuentoGravado)}</span>
          </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">IGV ({formatIgvRateLabel(igvRate)}):</span>
                  <span>{formatMoney(displayTotals.igv)}</span>
                </div>
                {displayTotals.noGravados > 0 && (
                  <div className="flex justify-between">
              <span className="text-gray-600">No gravados:</span>
                    <span>
                      {formatMoney(
                        (displayTotals.descuentoNoGravado ?? 0) > 0 &&
                          (displayTotals.noGravadosBruto ?? 0) > displayTotals.noGravados
                          ? displayTotals.noGravadosBruto
                          : displayTotals.noGravados,
                      )}
                    </span>
            </div>
          )}
                {(displayTotals.descuentoNoGravado ?? 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Descuento (no gravado):</span>
                    <span>- {formatMoney(displayTotals.descuentoNoGravado)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-200">
            <span>Total:</span>
                  <span className="text-teal-700">{formatMoney(displayTotals.total)}</span>
          </div>
                <DualCurrencyHint
                  currency={formData.currency || 'PEN'}
                  amount={displayTotals.total}
                  saleRate={saleRate}
                  className="pt-1"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => {
                      setAmountForm({ igv_amount: String(displayTotals.igv) });
                      setIsEditingAmounts(true);
                    }}
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                  >
                    Ajustar montos
                  </button>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Modifique el IGV para corregir diferencias por decimales. El total se recalcula.
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">IGV</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amountForm.igv_amount}
                    onChange={(e) => setAmountForm({ igv_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const igvNum = parseFloat(amountForm.igv_amount) || 0;
                      const taxableNet = displayTotals.baseImponibleNeta ?? displayTotals.baseImponible;
                      const totalRecalc =
                        Math.round((taxableNet + igvNum + displayTotals.noGravados) * 100) / 100;
                      setAmountOverride({
                        baseImponible: displayTotals.baseImponible,
                        baseImponibleNeta: taxableNet,
                        descuento: displayTotals.descuento ?? 0,
                        descuentoGravado: displayTotals.descuentoGravado ?? 0,
                        descuentoNoGravado: displayTotals.descuentoNoGravado ?? 0,
                        igv: igvNum,
                        noGravados: displayTotals.noGravados,
                        total: totalRecalc,
                      });
                      setIsEditingAmounts(false);
                    }}
                    className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-md"
                  >
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingAmounts(false)}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-3 border-b border-gray-200">
          Observaciones
        </h3>
        <textarea
          value={formData.notes}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
          disabled={readOnly}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Notas adicionales..."
        />
      </div>

      {hasServiceItems && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-5 pb-3 border-b border-gray-200">
            Fechas de Servicio
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Inicio del Servicio
              </label>
              <input
                type="date"
                value={formData.service_start_date}
                onChange={(e) => handleFieldChange('service_start_date', e.target.value)}
                disabled={readOnly}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.service_start_date || errors.delivery_date
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
              {errors.service_start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.service_start_date}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin del Servicio</label>
              <input
                type="date"
                value={formData.service_end_date}
                onChange={(e) => handleFieldChange('service_end_date', e.target.value)}
                disabled={readOnly}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.service_start_date || errors.delivery_date
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
            </div>
          </div>
        </div>
      )}

      {hasProductItems && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-5 pb-3 border-b border-gray-200">
            Fecha de Entrega
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Entrega de Productos
            </label>
            <input
              type="date"
              value={formData.delivery_date}
              onChange={(e) => handleFieldChange('delivery_date', e.target.value)}
              disabled={readOnly}
              className={`w-full md:w-80 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.delivery_date || errors.service_start_date
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
            />
            {errors.delivery_date && (
              <p className="mt-1 text-sm text-red-600">{errors.delivery_date}</p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium"
          disabled={loading}
        >
          {readOnly ? 'Volver' : 'Cancelar'}
        </button>
        {!readOnly && (
          <button
            type="submit"
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium disabled:opacity-50"
            disabled={
              loading ||
              (!isQuoteLike && formData.issue_date && closedShiftDates.includes(formData.issue_date))
            }
            title={
              !isQuoteLike && formData.issue_date && closedShiftDates.includes(formData.issue_date)
                ? 'El turno de esta fecha ya fue cerrado'
                : undefined
            }
          >
            {loading
              ? 'Guardando...'
              : entity?.id
                ? 'Actualizar'
                : isQuote
                  ? 'Crear cotización'
                  : isServiceOrder
                    ? 'Crear orden de servicio'
                    : 'Registrar venta'}
          </button>
        )}
      </div>
    </form>

      {isReferenceModalOpen ? (
      <VentasOrderReferenceSelectorModal
        isOpen={isReferenceModalOpen}
        onClose={() => setIsReferenceModalOpen(false)}
        onAccept={handleAcceptReferences}
        loading={referencesLoading}
        quotes={pendingReferenceQuotes}
        serviceOrders={pendingReferenceServiceOrders}
        continuousServices={pendingReferenceContinuousServices}
        exitGuides={pendingReferenceExitGuides}
        selectedQuoteIds={linkedQuotes.map((q) => q.id)}
        selectedServiceOrderIds={linkedServiceOrders.map((s) => s.id)}
        selectedContinuousServiceIds={linkedContinuousServices.map((s) => s.id)}
        selectedExitGuideIds={linkedExitGuides.map((g) => g.id)}
        onToggleQuote={handleToggleReferenceQuote}
        onToggleServiceOrder={handleToggleReferenceServiceOrder}
        onToggleContinuousService={handleToggleReferenceContinuousService}
        onToggleExitGuide={handleToggleReferenceExitGuide}
        clientName={selectedClientName}
        quotesOnly={isServiceOrder}
      />
      ) : null}

      <CreateSupplierModal
        isOpen={isCreateClientModalOpen}
        onClose={() => setIsCreateClientModalOpen(false)}
        onSuccess={handleClientCreated}
        forceProviderOnly={false}
        clientOnly
      />

      <ComprasItemsImportPreviewModal
        isOpen={!!importPreview}
        preview={importPreview}
        onClose={() => setImportPreview(null)}
        onConfirm={handleConfirmImportPreview}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        duration={toast.duration || 5000}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </>
  );
};

export default VentasCommercialForm;
